import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Avatar,
  IconButton,
  CircularProgress,
  useTheme,
  alpha,
  InputAdornment
} from '@mui/material';
import {
  Close,
  RocketLaunch,
  ContentCopy,
  CheckCircle,
  ArrowForward,
  Dashboard
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useCustomTheme } from '../../context/ThemeContext';
import { toast } from 'react-toastify';
import BookingApiService from '../../services/BookingApiService';
import apiService from '../../services/ApiService';
import { sanitizeText } from '../../utils/validation';

const INDUSTRY_OPTIONS = [
  { value: 'medical', label: 'Healthcare & Medical', theme: 'medical' },
  { value: 'beauty', label: 'Beauty & Spa', theme: 'beauty' },
  { value: 'fitness', label: 'Fitness & Wellness', theme: 'fitness' },
  { value: 'professional', label: 'Professional Services', theme: 'professional' },
  { value: 'restaurant', label: 'Food & Beverage', theme: 'restaurant' },
  { value: 'automotive', label: 'Automotive', theme: 'automotive' },
  { value: 'creative', label: 'Creative & Design', theme: 'creative' },
  { value: 'other', label: 'Other', theme: 'dark-maximalist' }
];

const SERVICE_TEMPLATES = {
  medical: { name: 'Consultation', duration: 30, price: 150 },
  beauty: { name: 'Haircut', duration: 45, price: 50 },
  fitness: { name: 'Personal Training', duration: 60, price: 80 },
  professional: { name: 'Consultation', duration: 60, price: 150 },
  restaurant: { name: 'Table for 2', duration: 90, price: 0 },
  automotive: { name: 'Oil Change', duration: 30, price: 50 },
  creative: { name: 'Design Consultation', duration: 60, price: 120 },
  other: { name: 'Service', duration: 60, price: 100 }
};

const DEFAULT_AVAILABILITY = {
  monday: { enabled: true, start: '09:00', end: '17:00' },
  tuesday: { enabled: true, start: '09:00', end: '17:00' },
  wednesday: { enabled: true, start: '09:00', end: '17:00' },
  thursday: { enabled: true, start: '09:00', end: '17:00' },
  friday: { enabled: true, start: '09:00', end: '17:00' },
  saturday: { enabled: false, start: '09:00', end: '17:00' },
  sunday: { enabled: false, start: '09:00', end: '17:00' }
};

const QuickLaunchFlow = ({ open, onClose, onComplete }) => {
  const theme = useTheme();
  const { setTheme } = useCustomTheme();
  const { currentUser, updateUserProfile } = useAuth();

  const [screen, setScreen] = useState('form'); // 'form' | 'success'
  const [loading, setLoading] = useState(false);
  const [businessName, setBusinessName] = useState('');
  const [industry, setIndustry] = useState('');
  const [bookingUrl, setBookingUrl] = useState('');
  const [copied, setCopied] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setScreen('form');
      setLoading(false);
      setBusinessName('');
      setIndustry('');
      setBookingUrl('');
      setCopied(false);
    }
  }, [open]);

  const generateSlug = (name) => {
    const base = sanitizeText(name).toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    const suffix = currentUser?.uid?.slice(0, 8) || '';
    return `${base}-${suffix}`;
  };

  const handleLaunch = async () => {
    if (!businessName.trim() || !industry) return;

    setLoading(true);
    try {
      const slug = generateSlug(businessName);
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
      const template = SERVICE_TEMPLATES[industry] || SERVICE_TEMPLATES.other;
      const industryTheme = INDUSTRY_OPTIONS.find(o => o.value === industry)?.theme || 'dark-maximalist';

      // 1. Save profile with onboarding flag
      await updateUserProfile({
        bookerOnboardingCompleted: true,
        bookerOnboardingCompletedAt: new Date().toISOString(),
        quickLaunchCompleted: true,
        businessName: sanitizeText(businessName),
        businessSlug: slug,
        industry,
        timezone,
        businessType: 'solo',
        interfaceMode: 'simple',
        hiddenFeatures: ['leads', 'automation', 'crm']
      });

      // 2. Apply industry theme
      await setTheme(industryTheme);

      // 3. Create first service
      try {
        const existingServices = await BookingApiService.getServices();
        if (!existingServices || existingServices.length === 0) {
          await BookingApiService.createService({
            name: template.name,
            duration: template.duration,
            price: template.price,
            description: `${template.name} service`,
            isActive: true,
            category: industry
          });
        }
      } catch (serviceError) {
        console.warn('Quick Launch: service creation skipped:', serviceError.message);
      }

      // 4. Save default availability
      await BookingApiService.updateAvailability(DEFAULT_AVAILABILITY);

      // 5. Report setup steps (fire-and-forget)
      ['business-profile', 'add-service', 'set-availability'].forEach(
        step => apiService.reportSetupStep(step)
      );

      setBookingUrl(`${window.location.origin}/book/${slug}`);
      setScreen('success');
      toast.success('Your booking page is live!');
    } catch (error) {
      console.error('Quick Launch error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(bookingUrl);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const isValid = businessName.trim().length >= 2 && industry;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          background: theme.palette.background.paper,
          overflow: 'hidden'
        }
      }}
    >
      <IconButton
        onClick={onClose}
        disabled={loading}
        sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
      >
        <Close />
      </IconButton>

      <DialogContent sx={{ p: 4, textAlign: 'center' }}>
        {screen === 'form' ? (
          <>
            <Avatar
              sx={{
                width: 80, height: 80, mx: 'auto', mb: 2,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
              }}
            >
              <RocketLaunch sx={{ fontSize: 40 }} />
            </Avatar>

            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Go Live in 30 Seconds
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
              Just your business name and industry. We'll set up everything else with smart defaults.
            </Typography>

            <Box sx={{ maxWidth: 400, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label="Business Name"
                placeholder="e.g., Jane's Hair Salon"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                disabled={loading}
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ opacity: 0.5 }}>
                        {'\u{1F3E2}'}
                      </Typography>
                    </InputAdornment>
                  )
                }}
              />

              <TextField
                fullWidth
                select
                label="Industry"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                disabled={loading}
              >
                {INDUSTRY_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>

              {industry && (
                <Box
                  sx={{
                    p: 1.5, borderRadius: 2,
                    bgcolor: alpha(theme.palette.info.main, 0.08),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    We'll create a <strong>{SERVICE_TEMPLATES[industry]?.name || 'Service'}</strong> ({SERVICE_TEMPLATES[industry]?.duration || 60} min
                    {SERVICE_TEMPLATES[industry]?.price > 0 ? `, $${SERVICE_TEMPLATES[industry]?.price}` : ', free'}) and set Mon-Fri 9-5 hours.
                    You can customize everything later.
                  </Typography>
                </Box>
              )}

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleLaunch}
                disabled={!isValid || loading}
                endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ArrowForward />}
                sx={{
                  py: 1.5, borderRadius: 3, fontWeight: 700, fontSize: '1.1rem',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`
                  }
                }}
              >
                {loading ? 'Setting up...' : 'Quick Launch'}
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Avatar
              sx={{
                width: 80, height: 80, mx: 'auto', mb: 2,
                bgcolor: 'success.main'
              }}
            >
              <CheckCircle sx={{ fontSize: 40 }} />
            </Avatar>

            <Typography variant="h4" fontWeight="bold" gutterBottom>
              You're Live!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Your booking page is ready. Share this link with clients:
            </Typography>

            <Box
              sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                p: 1.5, borderRadius: 2, mb: 3,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                maxWidth: 450, mx: 'auto'
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  flex: 1, fontFamily: 'monospace', wordBreak: 'break-all',
                  color: theme.palette.primary.main, fontWeight: 600
                }}
              >
                {bookingUrl}
              </Typography>
              <IconButton onClick={handleCopyLink} color={copied ? 'success' : 'primary'} size="small">
                {copied ? <CheckCircle /> : <ContentCopy />}
              </IconButton>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: 300, mx: 'auto' }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<Dashboard />}
                onClick={() => onComplete?.()}
                sx={{ borderRadius: 3, py: 1.2 }}
              >
                Go to Dashboard
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  onComplete?.('customize');
                }}
                sx={{ borderRadius: 3 }}
              >
                Customize Your Setup
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuickLaunchFlow;
